import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo } from "react";
import { getProductPriceView } from "@utils/product/get-product-discount";
import { EModalKeys, useModal } from "@components/helpers/modal-provider";
import { fitPopupInViewport } from "@components/ui-kit/utils/fit-popup-in-viewport";
import { HomeItemProductInfo, HomeModelItem, resolveResponsivePoint, resolveResponsiveSize } from "@entities/home/helpers";
import useAnimatedRouter from "@hooks/use-animated-router";
import useCustomerGroupId from "@hooks/use-customer-group-id";
import { useLayout } from "@hooks/use-layout";
import { BreakpointValue } from "@models/breakpoints";
const HERO_CARD_IMAGE = "/images/large-hero-card-media.png";
const HERO_CARD_LARGE_SIZE = {
  width: 468,
  height: 468
};
type TriggerRect = Pick<DOMRect, "left" | "top" | "width" | "height">;
type UseHomeItemControllerParams = {
  item: HomeModelItem;
  breakpoint: BreakpointValue;
  isCardOpen: boolean;
  activeOriginIndex: number;
  onShowCard: (itemId: number, x: number, y: number, originIndex: number) => void;
  onCloseCard: () => void;
};
export const useHomeItemController = t0 => {
  const $ = _c(65);
  const {
    item,
    breakpoint,
    isCardOpen,
    activeOriginIndex,
    onShowCard,
    onCloseCard
  } = t0;
  const {
    navigate
  } = useAnimatedRouter();
  const {
    openModal
  } = useModal();
  const {
    isMobile
  } = useLayout();
  const groupId = useCustomerGroupId();
  let t1;
  if ($[0] !== breakpoint || $[1] !== item.position) {
    t1 = resolveResponsivePoint(item.position, breakpoint);
    $[0] = breakpoint;
    $[1] = item.position;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const pos = t1;
  let t2;
  if ($[3] !== breakpoint || $[4] !== item.size) {
    t2 = resolveResponsiveSize(item.size, breakpoint);
    $[3] = breakpoint;
    $[4] = item.size;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const size = t2;
  let t3;
  if ($[6] !== item.size) {
    t3 = resolveResponsiveSize(item.size, 1920);
    $[6] = item.size;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const baseSize = t3;
  let t4;
  if ($[8] !== item.productInfo || $[9] !== item.productInfos) {
    t4 = item.productInfos?.length ? item.productInfos : item.productInfo ? [item.productInfo] : [];
    $[8] = item.productInfo;
    $[9] = item.productInfos;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const rawInfos = t4;
  let t5;
  if ($[11] !== groupId || $[12] !== rawInfos) {
    let t6;
    if ($[14] !== groupId) {
      t6 = productInfo => {
        if (!productInfo.prices) {
          return productInfo;
        }
        const priceView = getProductPriceView(productInfo.prices, productInfo.promo, groupId);
        return {
          ...productInfo,
          formattedPrice: priceView.formattedPrice,
          oldPrice: priceView.oldPrice,
          discountPercent: priceView.discountPercent
        };
      };
      $[14] = groupId;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    t5 = rawInfos.map(t6);
    $[11] = groupId;
    $[12] = rawInfos;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const productInfos = t5;
  const activeProductInfo = productInfos[activeOriginIndex] ?? productInfos[0];
  const t6 = `${pos.x}px`;
  const t7 = `${pos.y}px`;
  const t8 = `${size.width}px`;
  const t9 = `${size.height}px`;
  const t10 = String(isCardOpen ? 2 : 1);
  let t11;
  if ($[16] !== t10 || $[17] !== t6 || $[18] !== t7 || $[19] !== t8 || $[20] !== t9) {
    t11 = {
      "--home-item-left": t6,
      "--home-item-top": t7,
      "--home-item-width": t8,
      "--home-item-height": t9,
      "--home-item-z-index": t10
    };
    $[16] = t10;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const wrapperStyle = t11;
  let t12;
  if ($[22] !== baseSize.height || $[23] !== baseSize.width || $[24] !== breakpoint || $[25] !== isMobile || $[26] !== item.id || $[27] !== onShowCard || $[28] !== pos.x || $[29] !== pos.y || $[30] !== size.height || $[31] !== size.width) {
    t12 = (productInfo_0, originIndex, triggerRect) => {
      if (isMobile) {
        return;
      }
      const originPoint = resolveResponsivePoint(productInfo_0.origin, breakpoint);
      if (!originPoint) {
        return;
      }
      const {
        x,
        y
      } = originPoint;
      const shouldAutoPosition = productInfo_0.autoPosition !== false;
      const scaledX = shouldAutoPosition ? x * size.width / baseSize.width : x;
      const scaledY = shouldAutoPosition ? y * size.height / baseSize.height : y;
      const anchorInViewport = triggerRect ? {
        x: triggerRect.left,
        y: triggerRect.top
      } : {
        x: pos.x + scaledX,
        y: pos.y + scaledY
      };
      const popupInViewport = fitPopupInViewport({
        anchor: anchorInViewport,
        triggerSize: triggerRect ? {
          width: triggerRect.width,
          height: triggerRect.height
        } : {
          width: 0,
          height: 0
        },
        popupSize: HERO_CARD_LARGE_SIZE,
        viewport: {
          left: 0,
          top: 0,
          width: window.innerWidth,
          height: window.innerHeight
        }
      });
      onShowCard(item.id, popupInViewport.x, popupInViewport.y, originIndex);
    };
    $[22] = baseSize.height;
    $[23] = baseSize.width;
    $[24] = breakpoint;
    $[25] = isMobile;
    $[26] = item.id;
    $[27] = onShowCard;
    $[28] = pos.x;
    $[29] = pos.y;
    $[30] = size.height;
    $[31] = size.width;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const handleOpenCard = t12;
  let t13;
  if ($[33] !== handleOpenCard || $[34] !== isCardOpen || $[35] !== isMobile || $[36] !== onCloseCard || $[37] !== openModal) {
    t13 = (productInfo_1, originIndex_0, triggerRect_0) => {
      if (isMobile) {
        openModal(EModalKeys.HOME_HERO_CARD, {
          product: {
            image: productInfo_1.cardImage ?? HERO_CARD_IMAGE,
            name: productInfo_1.name,
            formattedPrice: productInfo_1.formattedPrice,
            oldPrice: productInfo_1.oldPrice,
            discountPercent: productInfo_1.discountPercent,
            isPresave: productInfo_1.isPresave
          },
          productSlug: productInfo_1.slug ?? null
        });
        return;
      }
      if (isCardOpen) {
        onCloseCard();
        return;
      }
      handleOpenCard(productInfo_1, originIndex_0, triggerRect_0);
    };
    $[33] = handleOpenCard;
    $[34] = isCardOpen;
    $[35] = isMobile;
    $[36] = onCloseCard;
    $[37] = openModal;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const handleTriggerClick = t13;
  let t14;
  if ($[39] !== activeOriginIndex || $[40] !== handleOpenCard || $[41] !== isCardOpen || $[42] !== isMobile || $[43] !== navigate) {
    t14 = (productInfo_2, originIndex_1, triggerRect_1) => {
      if (isMobile) {
        return;
      }
      if (isCardOpen) {
        if (activeOriginIndex === originIndex_1) {
          if (productInfo_2.slug) {
            navigate(`/p/${productInfo_2.slug}`);
          }
          return;
        }
        handleOpenCard(productInfo_2, originIndex_1, triggerRect_1);
        return;
      }
      handleOpenCard(productInfo_2, originIndex_1, triggerRect_1);
    };
    $[39] = activeOriginIndex;
    $[40] = handleOpenCard;
    $[41] = isCardOpen;
    $[42] = isMobile;
    $[43] = navigate;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  const handleTriggerEnter = t14;
  let t15;
  if ($[45] !== handleOpenCard || $[46] !== isCardOpen || $[47] !== isMobile) {
    t15 = (e, productInfo_3, originIndex_2) => {
      const isPointerPressed = e.buttons !== 0;
      if (isMobile || isCardOpen || isPointerPressed) {
        return;
      }
      handleOpenCard(productInfo_3, originIndex_2, e.currentTarget.getBoundingClientRect());
    };
    $[45] = handleOpenCard;
    $[46] = isCardOpen;
    $[47] = isMobile;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  const handleTriggerHover = t15;
  let t16;
  if ($[49] !== onCloseCard) {
    t16 = () => {
      onCloseCard();
    };
    $[49] = onCloseCard;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  const handleClose = t16;
  let t17;
  if ($[51] !== activeProductInfo?.slug || $[52] !== navigate) {
    t17 = () => {
      const slug = activeProductInfo?.slug;
      if (!slug) {
        return;
      }
      navigate(`/p/${slug}`);
    };
    $[51] = activeProductInfo?.slug;
    $[52] = navigate;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  activeProductInfo?.slug;
  const handleProductCardClick = t17;
  let t18;
  if ($[54] !== activeProductInfo || $[55] !== baseSize || $[56] !== handleClose || $[57] !== handleProductCardClick || $[58] !== handleTriggerClick || $[59] !== handleTriggerEnter || $[60] !== handleTriggerHover || $[61] !== productInfos || $[62] !== size || $[63] !== wrapperStyle) {
    t18 = {
      activeProductInfo,
      baseSize,
      handleClose,
      handleProductCardClick,
      handleTriggerClick,
      handleTriggerEnter,
      handleTriggerHover,
      productInfos,
      size,
      wrapperStyle
    };
    $[54] = activeProductInfo;
    $[55] = baseSize;
    $[56] = handleClose;
    $[57] = handleProductCardClick;
    $[58] = handleTriggerClick;
    $[59] = handleTriggerEnter;
    $[60] = handleTriggerHover;
    $[61] = productInfos;
    $[62] = size;
    $[63] = wrapperStyle;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  return t18;
};